import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { createElement, useEffect, useRef, useState } from "react";
import { icon } from "@/app/style/icons";

interface AnimatedSearch {
    value: string;
    setValue: (value: string) => void;
}

const AnimatedSearch = ({ value, setValue }: AnimatedSearch) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // Zustand für die Anzeige der Suchleiste
    const [expanded, setExpanded] = useState(false);
    // Zustand für Animation (fadeIn, fadeOut)
    const [animationState, setAnimationState] = useState<"fadeIn" | "fadeOut" | null>(null);

    // Verzögerter Autofokus mit 120ms nach dem Einblenden
    useEffect(() => {
        if (expanded) {
            const timeoutId = setTimeout(() => {
                if (inputRef && inputRef.current) {
                    inputRef.current.focus();
                }
            }, 120); // Etwas mehr Zeit für die Animation

            return () => clearTimeout(timeoutId);
        }
    }, [expanded, inputRef]);

    // Erweitern der Suche
    const expandSearch = () => {
        setAnimationState("fadeIn");
        setExpanded(true);
    };

    // Suche wieder einklappen
    const collapseSearch = () => {
        if (expanded) {
            setAnimationState("fadeOut");

            // Warte, bis die Animation abgeschlossen ist, bevor die Komponente tatsächlich eingeklappt wird
            setTimeout(() => {
                setExpanded(false);
                setAnimationState(null);
            }, 200); // Etwas weniger als die Animationsdauer (240ms)
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                position: "relative",
                width: expanded ? 200 : 26,
                height: 40,
                transition: "width 240ms ease-in-out",
                overflow: "hidden",
            }}
        >
            {!expanded ? (
                <IconButton onClick={expandSearch} size={"small"}>
                    <icon.search sx={{ color: "semantic.icon.functional" }} />
                </IconButton>
            ) : (
                <TextField
                    value={value}
                    size={"small"}
                    onChange={(event) => setValue(event.target.value)}
                    fullWidth={true}
                    placeholder={"Search"}
                    inputRef={inputRef}
                    autoFocus
                    onBlur={() => {
                        if (!value) {
                            collapseSearch();
                        }
                    }}
                    sx={{
                        width: "100%",
                        animation: `${animationState} 240ms ease-in-out`,
                        "@keyframes fadeIn": {
                            "0%": {
                                opacity: 0,
                            },
                            "100%": {
                                opacity: 1,
                            },
                        },
                        "@keyframes fadeOut": {
                            "0%": {
                                opacity: 1,
                            },
                            "100%": {
                                opacity: 0,
                            },
                        },
                    }}
                    slotProps={{
                        input: {
                            sx: {
                                height: 32,
                            },
                            endAdornment: (
                                <InputAdornment position={"end"} sx={{ mr: -1 }}>
                                    <IconButton
                                        size={"small"}
                                        onClick={() => {
                                            setValue("");
                                            setExpanded(false);
                                        }}
                                    >
                                        <icon.cancel sx={{ color: "semantic.icon.functional" }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default AnimatedSearch;
